"use client";

import type { ShipmentItem } from "@/app/encomendas/types";

/**
 * Ownership of the package data across the encomendas flow.
 *
 * Step 1 ("Pacotes", /encomendas) owns BASE_KEY: dimensions plus the coarse
 * per-item weightVolume/unitValueBRL it collects. It is the only writer.
 *
 * Step 2 ("Resumo", /encomendas/pacotes) owns RESUMO_KEY: the same packages
 * enriched with the fields only that screen collects — item name, quantity,
 * weightValue and weightUnit. Step 1 must NEVER read these back: its quote
 * payload always assumes quantity 1, so a leaked quantity silently multiplies
 * the quoted weight/value.
 *
 * The resumo snapshot is stamped with a signature of the base snapshot it was
 * derived from. If step 1 later changes dimensions or the package/item set, the
 * signature stops matching and the stale resumo data is ignored.
 */
const BASE_KEY = "embarca-log.packages";
const RESUMO_KEY = "embarca-log.packages-resumo";

export type StoredPackageLike = {
  id: string;
  name?: string;
  widthCm: number;
  heightCm: number;
  lengthCm: number;
  weightKg?: number;
  unitValueBRL?: number;
  quantity?: number;
  selectedPresetId?: string;
  items?: ShipmentItem[];
};

type ResumoSnapshot = {
  signature: string;
  packages: unknown[];
};

const readJSON = <T>(key: string): T | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const writeJSON = (key: string, value: unknown): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

/** Identity of a base snapshot: package ids, dimensions and item ids. */
export const packagesSignature = (pkgs: StoredPackageLike[] | null): string =>
  JSON.stringify(
    (pkgs ?? []).map((p) => [
      p.id,
      p.lengthCm ?? 0,
      p.widthCm ?? 0,
      p.heightCm ?? 0,
      (p.items ?? []).map((i) => i.id),
    ])
  );

/**
 * Drops the fields the Resumo screen owns — item name and quantity — so step 1
 * can hydrate from a snapshot written by an older build (or by a future writer)
 * without ever picking up a quantity other than 1.
 *
 * Weight and declared value are NOT stripped: step 1 collects them itself
 * (weightValue/weightUnit feed its "Peso" input), so clearing them would blank
 * the form on back-navigation.
 */
export const stripResumoFields = (
  items: ShipmentItem[] | undefined | null
): ShipmentItem[] =>
  (items ?? []).map((item) => ({
    id: item.id,
    name: "",
    quantity: 1,
    weightVolume: item.weightVolume ?? "",
    weightValue: item.weightValue,
    weightUnit: item.weightUnit ?? "kg",
    unitValueBRL: item.unitValueBRL ?? 0,
  }));

export const readBasePackages = <T = StoredPackageLike>(): T[] | null => {
  const parsed = readJSON<T[]>(BASE_KEY);
  return Array.isArray(parsed) ? parsed : null;
};

export const writeBasePackages = (pkgs: StoredPackageLike[]): void => {
  writeJSON(BASE_KEY, pkgs);
};

export const hasBasePackages = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return !!localStorage.getItem(BASE_KEY);
  } catch {
    return false;
  }
};

/** Returns the Resumo snapshot only while it still matches the base snapshot. */
export const readResumoPackages = <T = StoredPackageLike>(): T[] | null => {
  const snapshot = readJSON<ResumoSnapshot>(RESUMO_KEY);
  if (!snapshot || !Array.isArray(snapshot.packages)) return null;
  if (snapshot.signature !== packagesSignature(readBasePackages())) return null;
  return snapshot.packages as T[];
};

export const writeResumoPackages = (pkgs: StoredPackageLike[]): void => {
  writeJSON(RESUMO_KEY, {
    signature: packagesSignature(readBasePackages()),
    packages: pkgs,
  } satisfies ResumoSnapshot);
};

/**
 * What steps 3 and 4 (envio, dados, pagamento) should read: the enriched
 * Resumo snapshot when it is still valid, otherwise the base one.
 */
export const readFlowPackages = <T = StoredPackageLike>(): T[] | null =>
  readResumoPackages<T>() ?? readBasePackages<T>();

export const clearPackages = (): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(BASE_KEY);
    localStorage.removeItem(RESUMO_KEY);
  } catch {}
};
